import React, { ReactElement } from 'react';
import { styled } from '@mui/material/styles';
import { Alert, AlertColor, Box, Link, Snackbar } from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';

import { useTranslation } from 'react-i18next';
import { GridCellParams } from '@mui/x-data-grid';
import Toolbar from '../Toolbar';
import DataTable from '../DataTable';
import * as providerSlice from '../../../data/provider.slice';
import * as driverSlice from '../../../data/driver.slice';
import * as userSlice from '../../../data/user.slice';
import { SortModel } from '../../../data/models/sort-model.model';
import DriverDialog from './DriverDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import tableUtils from '../../../services/tableUtils';
import { Provider } from '../../../data/models/provider.model';
import { useCanDeleteDriver } from '../../../hooks/permissions/useCanDeleteDriver';
import { useCanEditDriver } from '../../../hooks/permissions/useCanEditDriver';
import { useCanCreateDriver } from '../../../hooks/permissions/useCanCreateDriver';
import OnboardingDriverDataTable from './OnboardingDriverDataTable';
import { Driver } from '../../../data/models/driver.model';
import driverUtils from '../../../services/driverUtils';
import driverService from '../../../services/driverService';
import providerService from '../../../services/providerService';
import phoneUtils from '../../../services/phoneUtils';
import { PhoneNumber } from '../../../data/models/phone-number.model';

const PREFIX = 'Drivers';

const classes = {
  container: `${PREFIX}-container`,
  search: `${PREFIX}-search`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

interface DriversProps {
  provider: Provider | null;
  canCreate?: boolean | false;
}

export default function Drivers({ provider, canCreate }: DriversProps) {
  const navigate = useNavigate();

  const { t } = useTranslation('common');
  const canCreateDriver = useCanCreateDriver();
  const canEditDriver = useCanEditDriver();
  const canDeleteDriver = useCanDeleteDriver();

  const [selectedProvider, setSelectedProvider] =
    React.useState<Provider | null>(null);

  React.useEffect(() => {
    if (provider) {
      setSelectedProvider(provider);
    }
  });
  const selectedFilter = useAppSelector(driverSlice.selectSelectedFilter);
  const searchFilter = useAppSelector(driverSlice.selectSearchFilter);
  const providerDriverSearchFilter = useAppSelector(
    providerSlice.selectDriverSearchFilter,
  );
  const page = useAppSelector(driverSlice.selectPage);
  const pageSize = useAppSelector(driverSlice.selectPageSize);
  const sortModel = useAppSelector(driverSlice.selectSortModel);
  const loading = useAppSelector(driverSlice.selectLoadingDriverList);
  const driverList = useAppSelector(driverSlice.selectDriverList);
  const onboardingDrivers = useAppSelector(driverSlice.selectOnboardingDrivers);
  const roles = useAppSelector(driverSlice.selectUserRoleIds);

  const driver = useAppSelector(driverSlice.selectDriver);
  const importingDriver = useAppSelector(driverSlice.selectImportingDrivers);
  const importDriversError = useAppSelector(
    driverSlice.selectImportDriversError,
  );
  const importDriversSuccess = useAppSelector(
    driverSlice.selectImportDriversSuccess,
  );
  const userSnackbarMessage = useAppSelector(
    userSlice.selectUserSnackbarMessage,
  );

  const driverSnackbarMessage = useAppSelector(
    driverSlice.selectDriverSnackbarMessage,
  );
  const driverSnackbarError = useAppSelector(
    driverSlice.selectDriverSnackbarError,
  );

  const userSnackbarError = useAppSelector(userSlice.selectUserSnackbarError);
  const dispatch = useAppDispatch();

  const [editorOpen, setEditorOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    const filter = '';
    const search = provider ? providerDriverSearchFilter : searchFilter;

    dispatch(
      driverSlice.fetchDriverListAsync(
        pageSize,
        page,
        filter,
        search,
        sortModel,
        true,
        provider?.transportationProviderID,
      ),
    );
    dispatch(
      driverSlice.fetchDriverListAsync(
        1000,
        1,
        filter,
        search,
        null,
        false,
        provider?.transportationProviderID,
      ),
    );
  }, [
    provider,
    selectedFilter,
    searchFilter,
    providerDriverSearchFilter,
    page,
    pageSize,
    sortModel,
    dispatch,
  ]);

  React.useEffect(() => {
    if (importDriversError) {
      setSnackbarMessage(importDriversError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [importDriversError]);

  React.useEffect(() => {
    if (userSnackbarMessage) {
      setSnackbarMessage(userSnackbarMessage);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      dispatch(userSlice.setUserSnackbarMessage(null));
    } else if (userSnackbarError) {
      setSnackbarMessage(userSnackbarError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [userSnackbarMessage, userSnackbarError]);

  React.useEffect(() => {
    if (driverSnackbarMessage) {
      setSnackbarMessage(driverSnackbarMessage);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      dispatch(driverSlice.setDriverSnackbarMessage(null));
    } else if (driverSnackbarError) {
      setSnackbarMessage(driverSnackbarError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      dispatch(driverSlice.setDriverSnackbarError(null));
    }
  }, [driverSnackbarMessage, driverSnackbarError]);

  React.useEffect(() => {
    if (importDriversSuccess) {
      setSnackbarMessage(importDriversSuccess);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      // reloadDrivers();
    }
  }, [importDriversSuccess, /*reloadDrivers,*/ dispatch]);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState<string | null>('');
  const [deleteId, setDeleteId] = React.useState<Driver | undefined>();
  const [deleteFromOnboardTable, setDeleteFromOnboardTable] =
    React.useState(false);

  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<
    AlertColor | undefined
  >();
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const renderUsername = (params: GridCellParams): ReactElement => {
    return <span>{params.row.preferredUserName}</span>;
  };

  const renderDriverLastName = (params: GridCellParams): ReactElement => {
    return (
      <Link
        href="#"
        onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          evt.preventDefault();
          navigate('/drivers/' + params.row.driverID);
        }}
        color="textPrimary"
        underline="always">
        {params.row.driverLastName}
      </Link>
    );
  };

  const renderDriverPhone = (params: GridCellParams): ReactElement => {
    /**
     * The current api response returns PhoneNumbers as the array of
     * Phone model, not full PhoneNumber model.
     * So, it's hard to determine which phone number is actually primary
     * phone number since Phone model doesn't include `IsPrimaryPhoneNumber` attribute.
     * Therefore, the first element of PhoneNumbers array is considered
     * as primary number at the moment.
     */
    let phoneNumber: PhoneNumber | undefined;
    if (params.row.phoneNumbers?.length > 0) {
      phoneNumber = params.row.phoneNumbers[0];
    }
    return <>{phoneUtils.formatPhone(phoneNumber)}</>;
  };

  const renderDriverEmail = (params: GridCellParams): ReactElement => {
    /**
     * The current api response returns the driver primary email address
     * as an array of strings, not full DriverEmail model.
     * We can refactor this once the api response is updated in the future.
     */
    let email = '';
    if (params.row.emailAddresses?.length > 0) {
      email = params.row.emailAddresses[0];
    }
    return <>{email}</>;
  };

  const deleteRow = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    params: GridCellParams,
  ) => {
    deleteDriver(
      evt,
      params.row,
      params.row.driverFirstName,
      params.row.driverLastName,
    );
  };

  const deleteDriver = (
    evt: any,
    id: Driver,
    firstName: string,
    lastName: string,
    fromOnboardTable = false,
  ) => {
    evt.preventDefault();
    setDeleteId(id);
    setDeleteFromOnboardTable(fromOnboardTable);
    setConfirmMessage(t('drivers.confirmDelete', { firstName, lastName }));
    setConfirmOpen(true);
  };

  const deleteDriverFromOnboardTable = (
    evt: any,
    id: Driver,
    firstName: string,
    lastName: string,
  ) => {
    deleteDriver(evt, id, firstName, lastName, true);
  };

  const confirmDeleteDriver = () => {
    setConfirmOpen(false);
    if (deleteId) {
      dispatch(
        driverSlice.deleteDriverAsync(
          provider?.transportationProviderID,
          deleteId,
          !deleteFromOnboardTable,
        ),
      );
    }
  };

  const cancelDeleteDriver = () => {
    setConfirmOpen(false);
  };

  const onPasswordReset = (driver: any) => {
    const userAccountID = driver.userAccountID!;

    // Drivers list have array of email address as an array of string at the moment,
    // So, the first element of array is the driver's email address
    const email = driver.emailAddresses[0];
    if (userAccountID && email) {
      dispatch(userSlice.resetUserPasswordAsync(userAccountID, email));
    }
  };

  const sendActivationEmail = (driver: any, state: number = 2) => {
    const payload = driverUtils.resendEmailPayload(
      [driver],
      state,
      undefined,
      roles,
    );
    dispatch(driverSlice.sendActivationEmailsAsync(payload));
  };
  const onDriverEdit = (driver: any) => {
    if (!provider) {
      if (driver?.transportationProviderID) {
        providerService
          .getProvider(driver.transportationProviderID)
          .subscribe((d: any) => {
            setSelectedProvider(d);
          });
      }
    }

    driverService.getDriver(driver.driverID).subscribe((d) => {
      setSelectEditDriver(d);
      setEditorOpen(true);
    });
  };

  const columns = tableUtils.getDriverColumnDefinition(
    renderDriverLastName,
    renderDriverPhone,
    renderDriverEmail,
    renderUsername,
    t,
    canDeleteDriver ? deleteRow : null,
    canEditDriver ? onPasswordReset : null,
    canEditDriver ? onDriverEdit : null,
  );

  const onSearchChange = (val: string) => {
    if (provider) {
      dispatch(providerSlice.setDriverSearchFilter(val));
    } else {
      dispatch(driverSlice.setSearchFilter(val));
    }
  };

  const onSelectedFilterChange = (val: string) => {
    dispatch(driverSlice.setSelectedFilter(val));
  };

  const onPageChange = (pg: number) => {
    dispatch(driverSlice.setPage(pg));
  };

  const onPageSizeChange = (pgSize: number) => {
    dispatch(driverSlice.setPageSize(pgSize));
  };

  const onSortModelChange = (model: SortModel | null) => {
    dispatch(driverSlice.setSortModel(model));
  };

  const openDriverEditor = () => {
    setEditorOpen(true);
    setSelectEditDriver(null);
  };

  const closeDriverEditor = (saved: boolean) => {
    dispatch(driverSlice.resetSavingFlags());
    dispatch(driverSlice.setValidateFields(''));
    setEditorOpen(false);

    if (saved) {
      const filter = searchFilter;
      const search = provider ? providerDriverSearchFilter : searchFilter;
      dispatch(
        driverSlice.fetchDriverListAsync(
          pageSize,
          page,
          filter,
          search,
          sortModel,
          true,
          provider?.transportationProviderID,
        ),
      );
      dispatch(
        driverSlice.fetchDriverListAsync(
          1000,
          1,
          filter,
          search,
          null,
          false,
          provider?.transportationProviderID,
        ),
      );
    }

    setSelectEditDriver(null);
  };

  const importDrivers = (file: File) => {
    if (provider?.transportationProviderID) {
      dispatch(
        driverSlice.importDriversAsync(
          provider?.transportationProviderID,
          file,
        ),
      );
    }
  };

  const sendDriverActivationEmails = (drivers: any[], err: boolean) => {
    const payload = driverUtils.resendEmailPayload(
      drivers,
      2,
      undefined,
      roles,
    );
    dispatch(driverSlice.sendActivationEmailsAsync(payload, err));
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
    dispatch(driverSlice.resetImportFlags());
  };

  const user = useAppSelector(userSlice.selectUserInfo);

  React.useEffect(() => {
    if (driver?.transportationProviderID && !provider) {
      dispatch(providerSlice.getProviderAsync(driver.transportationProviderID));
    }
  }, [driver, dispatch]);

  const [selectEditDriver, setSelectEditDriver] = React.useState<Driver | null>(
    null,
  );

  return (
    <StyledBox className={classes.container}>
      <Toolbar
        button={
          provider && (canCreate || canCreateDriver)
            ? t('drivers.add')
            : undefined
        }
        importButton={
          provider && (canCreate || canCreateDriver)
            ? t('drivers.import')
            : undefined
        }
        disableImport={importingDriver}
        filterOptions={undefined}
        currentFilter={selectedFilter}
        searchIcon={<PersonIcon />}
        searchPlaceholder={t('drivers.search')}
        currentSearchString={
          provider ? providerDriverSearchFilter : searchFilter
        }
        onFilterChange={onSelectedFilterChange}
        onSearchChange={onSearchChange}
        onCreate={openDriverEditor}
        onImport={importDrivers}
      />

      <DataTable
        getRowId={(row) => row.driverID}
        columns={columns}
        itemList={driverList}
        pageSize={pageSize}
        sortModel={sortModel}
        loading={loading}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortModelChange={onSortModelChange}
      />

      {user?.access?.userRoles.length === 1 &&
      user?.access?.userRoles.includes('tp.internal.user') ? (
        <OnboardingDriverDataTable
          drivers={onboardingDrivers}
          onDelete={canDeleteDriver ? deleteDriverFromOnboardTable : undefined}
          onSendActivationEmails={
            canCreateDriver ? sendDriverActivationEmails : undefined
          }
          onClickEditButton={onDriverEdit}
          loading={loading}
        />
      ) : onboardingDrivers.length < 1 ? null : (
        <OnboardingDriverDataTable
          drivers={onboardingDrivers}
          onDelete={canDeleteDriver ? deleteDriverFromOnboardTable : undefined}
          onSendActivationEmails={
            canCreateDriver ? sendDriverActivationEmails : undefined
          }
          onClickEditButton={onDriverEdit}
          onSendActivationEmail={sendActivationEmail}
          loading={loading}
        />
      )}

      {selectEditDriver === null && selectedProvider && editorOpen ? (
        <DriverDialog
          title={t('drivers.create')}
          open={editorOpen}
          provider={selectedProvider}
          close={closeDriverEditor}
        />
      ) : null}

      {selectedProvider && selectEditDriver && editorOpen ? (
        <DriverDialog
          title={t('drivers.create')}
          open={editorOpen}
          driver={selectEditDriver}
          provider={selectedProvider}
          close={closeDriverEditor}
        />
      ) : null}
      <ConfirmationDialog
        title={t('common.confirmDelete')}
        message={confirmMessage || ''}
        open={confirmOpen}
        positiveAction={confirmDeleteDriver}
        negativeAction={cancelDeleteDriver}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => closeSnackbar()}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
}
